import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import CubMascot from '../components/CubMascot.jsx';
import CourseHighlights from '../components/CourseHighlights.jsx';
import { Loader, Alert, Confetti, formatDate } from '../components/ui.jsx';

const rupees = (paise) => `₹${(paise / 100).toFixed(2)}`;


/* What each course is and what it gives you. Keyed by slug, because
   there are two of them on sale now and they are not alike. */
const COURSES = {
  junior: {
    name: 'Cyber Smart Kids',
    strap: 'Internet Safety & Phishing Awareness for Students',
    home: '/learn/course/junior',
    perks: [
      { icon: '📦', text: '9 modules, 45 interactive lessons' },
      { icon: '🎮', text: 'Games, stories and hands-on challenges' },
      { icon: '🏅', text: '21 badges to collect' },
      { icon: '🎨', text: 'Practical projects saved to your account' },
    ],
  },
  'ethical-hacker': {
    name: 'CyberCub: Ethical Hacker',
    strap: 'The advanced course — how attacks really work, and how to stop them',
    home: '/learn/course/ethical-hacker',
    perks: [
      { icon: '📦', text: '3 modules, 14 lessons for ages 11+' },
      { icon: '🖥️', text: 'Hands-on labs: Windows, Linux and macOS command lines' },
      { icon: '🎬', text: 'Narrated animated explainers with subtitles' },
      { icon: '🏅', text: '17 badges, and practicals that behave like the real thing' },
    ],
  },
};

export default function Checkout() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [params] = useSearchParams();
  const slug = COURSES[params.get('course')] ? params.get('course') : 'junior';
  const course = COURSES[slug];

  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null);

  const load = () => api.paymentStatus(slug).then(setStatus).catch((err) => setError(err.message));
  useEffect(() => {
    setStatus(null);
    setPaid(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  /* ---------------- route 1: Razorpay Checkout ---------------- */
  const payWithCheckout = async () => {
    setError('');
    if (!window.Razorpay) {
      setError('The payment window could not load. Check your connection and refresh the page.');
      return;
    }

    setBusy(true);
    let order;
    try {
      order = await api.createOrder(slug);
    } catch (err) {
      setError(err.message);
      setBusy(false);
      return;
    }

    const rzp = new window.Razorpay({
      // Always the key that created THIS order. Using a build-time key
      // here lets server and client drift apart (live order opened with
      // a test key), which Razorpay rejects with "Error in opening
      // checkout".
      key: order.keyId,
      order_id: order.orderId,
      amount: order.amount,
      currency: order.currency,
      name: 'CyberCub',
      // The course actually being bought, not a fixed string - the modal
      // used to say "Think Before You Click" even for Ethical Hacker.
      description: `${course.name.replace(/^CyberCub:\s*/, '')} — 1 month`,
      theme: { color: '#7c4dff' },
      prefill: { name: order.prefill?.name ?? user.fullName },
      handler: async (response) => {
        try {
          setPaid(
            await api.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          );
        } catch (err) {
          setError(err.message);
        } finally {
          setBusy(false);
        }
      },
      modal: {
        ondismiss: () => {
          setBusy(false);
          setError('Payment cancelled — you have not been charged. You can try again any time.');
        },
      },
    });

    rzp.on('payment.failed', (resp) => {
      setBusy(false);
      const reason = resp?.error?.description || 'The payment did not go through.';
      setError(`${reason} You have not been charged.`);
      api.paymentFailed({ razorpay_order_id: order.orderId, reason }).catch(() => {});
    });

    rzp.open();
  };

  if (error && !status) return <Alert kind="bad">{error}</Alert>;
  if (!status) return <Loader label="Loading your plan…" />;

  /* ---------------- paid ---------------- */
  if (paid || (status.hasAccess && status.reason === 'subscription')) {
    const until = paid?.validUntil ?? status.validUntil;
    return (
      <div className="checkout-done">
        <Confetti run={Boolean(paid)} />
        <CubMascot avatar={user.avatar} mood="cheer" size={150} className="anim-float" />
        <h1>{paid ? 'Payment complete! 🎉' : 'You are all set!'}</h1>
        <p className="muted">
          Your access to <strong>{course.name}</strong> is active until{' '}
          <strong>{formatDate(until)}</strong>.
        </p>
        <Link to={course.home} className="btn btn-lg mt-md">Start learning →</Link>
      </div>
    );
  }

  /* ---------------- a test account ---------------- */
  if (status.hasAccess && status.reason === 'test') {
    return (
      <div className="checkout-done">
        <CubMascot avatar={user.avatar} mood="star" size={140} />
        <h1>Test account 🧪</h1>
        <p className="muted">
          This login reaches every course without paying, so there is nothing to buy here. It is
          for checking how the paid experience behaves — not a subscription.
        </p>
        <Link to={course.home} className="btn btn-lg mt-md">Open {course.name} →</Link>
      </div>
    );
  }

  /* ---------------- covered by school ---------------- */
  if (status.hasAccess && status.reason === 'school') {
    return (
      <div className="checkout-done">
        <CubMascot avatar={user.avatar} mood="star" size={140} />
        <h1>Your school has you covered 🏫</h1>
        <p className="muted">
          Your account was set up by your school, so there is nothing to pay.
        </p>
        <Link to="/learn" className="btn btn-lg mt-md">Go to my courses →</Link>
      </div>
    );
  }

  const { plan } = status;

  return (
    <div className="checkout">
      <div className="page-head">
        <div>
          <h1>🔓 Unlock your course</h1>
          <p>One last step, {user.fullName.split(' ')[0]} — then all 45 lessons are yours.</p>
        </div>
      </div>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      <div className="checkout-grid">
        <section className="plan-card card">
          <span className="plan-badge">Monthly plan</span>
          <h2>{course.name}</h2>
          <p className="muted small">{course.strap}</p>

          <div className="plan-price">
            <span className="plan-amount">{rupees(plan.amountPaise)}</span>
            <span className="plan-period">per month</span>
          </div>

          <p className="small muted" style={{ marginBottom: '1rem' }}>
            Pay securely in the Razorpay window with a card, UPI or netbanking. Your access opens
            the moment the payment is confirmed — no waiting.
          </p>

          <button
            type="button"
            className="btn btn-lg"
            style={{ width: '100%' }}
            onClick={payWithCheckout}
            disabled={busy || !status.checkoutAvailable}
          >
            {busy ? 'Opening payment…' : `Pay ${rupees(plan.amountPaise)} securely →`}
          </button>
          {!status.checkoutAvailable && (
            <p className="small faint tc mt-md">
              Payments are not configured on this server yet.
            </p>
          )}

          <p className="plan-note small">
            🔒 Payments handled by Razorpay. CyberCub never sees your card details.
            Access lasts {plan.days} days and does not renew automatically.
          </p>

          <button type="button" className="btn btn-quiet" onClick={() => navigate('/learn')}>
            Maybe later
          </button>

          {status.mode === 'test' && (
            <details className="demo-box" style={{ marginTop: '1rem' }}>
              <summary>Test mode — how to pay</summary>
              <p className="small" style={{ margin: '0.5rem 0' }}>
                This is a Razorpay <strong>test</strong> key, so no real money moves and QR codes
                inside the checkout window cannot be scanned. Use:
              </p>
              <ul className="small" style={{ margin: 0, paddingLeft: '1.1rem' }}>
                <li><strong>UPI</strong> — type the VPA <code>success@razorpay</code>, do not scan</li>
                <li><strong>Card</strong> — <code>4111 1111 1111 1111</code>, any future expiry, any CVV</li>
                <li><strong>Netbanking</strong> — any bank, then press Success</li>
              </ul>
            </details>
          )}
        </section>

        <aside className="checkout-side">
          <div className="card-flat">
            <CubMascot avatar="cub-sun" mood="star" size={72} />
            <h3>What you get</h3>
            <ul className="plan-perks" style={{ textAlign: 'left', marginBottom: 0 }}>
              {course.perks.map((p) => (
                <li key={p.text}>
                  <span aria-hidden="true">{p.icon}</span> {p.text}
                </li>
              ))}
            </ul>
          </div>

          {status.history?.length > 0 && (
            <div className="card-flat">
              <h3>Your payments</h3>
              <ul className="login-history">
                {status.history.slice(0, 5).map((h, i) => (
                  <li key={i}>
                    <span className="small">
                      💳 {formatDate(h.created_at, true)}
                    </span>
                    <span
                      className={`pill ${
                        h.status === 'paid'
                          ? 'pill-good'
                          : h.status === 'pending'
                            ? 'pill-warn'
                            : h.status === 'created'
                              ? ''
                              : 'pill-bad'
                      }`}
                    >
                      {rupees(h.amount_paise)} · {h.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      <CourseHighlights heading="What you are unlocking" />
    </div>
  );
}
